/* eslint-disable no-restricted-syntax */
import { FormField, FormFieldDataSet, FormFieldGroup } from '../../forms-ui-form-faas.interface';
import { CascadeNode } from './cascade-node';

export class CascadeTree {
	private rootNodes: CascadeNode[] = [];

	/** Adds a node to the tree based on the FormField structure */
	addNode(formField: FormField) {
		if (formField.control === 'Dropdown') {
			if (formField.cascadeRank === 0) {
			// Root-level nodes (rank 0) are added directly to rootNodes with data from dataSets
				formField.dataSets.forEach((dataSet: FormFieldDataSet) => {
					const rootNode = this.findOrCreateChild(
						this.rootNodes,
						formField.name,
						dataSet.label,
						dataSet.value,
						formField.defaultText,
						formField.cascadeRank,
					);
					this.rootNodes.push(rootNode);
				});
			} else {
			// For ranks > 0, add nodes as children to the appropriate parent nodes
				formField.dataSets.forEach((dataSet) => {
					this.addChildNode(this.rootNodes, formField, dataSet, formField.groups);
				});
			}
		} else {
			// eslint-disable-next-line no-console
			console.error(`invalid formField: ${formField.control} being used to create cascade tree`);
		}
	}

	/** Recursively adds a child node to the correct parent based on matching group */
	private addChildNode(
		nodes: CascadeNode[],
		formField: FormField,
		dataSet: FormFieldDataSet,
		groups: FormFieldGroup[],
	) {
		nodes.forEach((node) => {
			if (groups.some((group) => group.name === node.value && group.name === dataSet.group)) {
				// Add new node as a child of the matched parent node
				const childNode = this.findOrCreateChild(
					node.children,
					dataSet.group,
					dataSet.label,
					dataSet.value,
					formField.defaultText,
					formField.cascadeRank,
				);
				node.addChild(childNode);
			} else if (node.children.length > 0) {
				// Recurse into child nodes to find the correct parent node
				this.addChildNode(node.children, formField, dataSet, groups);
			}
		});
	}

	/** Finds an existing child node or creates a new one if not found */
	private findOrCreateChild(
		children: CascadeNode[],
		name: string,
		label: string,
		value: string,
		defaultText: string,
		rank: number,
	): CascadeNode {
		// Search for an existing child with the same label and value
		let existingNode = children.find((child) => child.label === label && child.value === value);

		// If it exists, return it; otherwise, create a new node and add it
		if (!existingNode) {
			existingNode = new CascadeNode(name, label, value, defaultText, rank);
		}

		return existingNode;
	}

	/** Get nodes at a specific rank (level) in the tree */
	getOptionsAtRank(rank: number): CascadeNode[] {
		if (rank === 0) {
			// Return shallow copies of root nodes without children
			return this.rootNodes.map((node) => ({ ...node, children: [] } as CascadeNode));
		}

		const findNodesAtRank = (currentNodes: CascadeNode[], currentRank: number): CascadeNode[] => {
			if (currentRank === rank - 1) {
				return currentNodes.map((node) => ({
					...node,
					children: node.children.map((child) => ({ ...child, children: [] } as CascadeNode)),
				} as CascadeNode));
			}
			return currentNodes.flatMap((node) => findNodesAtRank(node.children, currentRank + 1));
		};

		return findNodesAtRank(this.rootNodes, 0);
	}

	/** Get the maximum rank (depth) of the tree */
	getTreeMaxRank(): number {
		let maxRank = 0;

		const traverse = (nodes: CascadeNode[]) => {
			nodes.forEach((node) => {
				if (node.rank > maxRank) maxRank = node.rank;
				if (node.children.length > 0) {
					traverse(node.children);
				}
			});
		};

		traverse(this.rootNodes);
		return maxRank;
	}

	/** Filter nodes by a specific key and rank to return a parent and its direct descendant */
	filterByKeyAndRank(key: string, rank: number): CascadeNode[] {
		const result: CascadeNode[] = [];

		const filterNodes = (nodes: CascadeNode[]) => {
			nodes.forEach((node) => {
				if (node.rank === rank && node.value === key) {
					result.push(node); // Push the full node with descendants
				} else if (node.children.length > 0) {
					filterNodes(node.children);
				}
			});
		};

		filterNodes(this.rootNodes);
		return result;
	}
}
